/**
 * MessageList
 *
 * Scrollable list of messages/threads with select-all toggle and empty state.
 */

import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Inbox } from 'lucide-react';
import { MessageListItem } from './MessageListItem';
import type { EmailMessage } from '@/lib/api';

interface MessageListProps {
  messages: EmailMessage[];
  loading: boolean;
  selectedIds: Set<string>;
  activeMessageId: string | null;
  onSelectMessage: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onClickMessage: (id: string) => void;
  onToggleStar: (id: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

export function MessageList({
  messages,
  loading,
  selectedIds,
  activeMessageId,
  onSelectMessage,
  onSelectAll,
  onClickMessage,
  onToggleStar,
}: MessageListProps) {
  if (loading) {
    return (
      <div className="flex flex-col gap-1 p-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-3">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-3 w-48" />
              <Skeleton className="h-2.5 w-64" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-20">
        <Inbox className="h-12 w-12 mb-3 opacity-40" />
        <p className="text-sm font-medium">No messages</p>
        <p className="text-xs mt-1">This folder is empty</p>
      </div>
    );
  }

  const allSelected = messages.length > 0 && messages.every((m) => selectedIds.has(m.id));

  return (
    <div className="flex flex-col h-full">
      {/* Select-all header */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/30">
        <Checkbox
          checked={allSelected}
          onCheckedChange={(checked) => onSelectAll(!!checked)}
          aria-label="Select all messages"
        />
        <span className="text-xs text-muted-foreground">
          {selectedIds.size > 0 ? `${selectedIds.size} selected` : `${messages.length} messages`}
        </span>
      </div>

      {/* Message rows */}
      <div className="flex-1 overflow-y-auto">
        {messages.map((msg) => (
          <MessageListItem
            key={msg.id}
            id={msg.id}
            fromName={msg.fromName}
            fromAddress={msg.fromAddress}
            subject={msg.subject}
            snippet={msg.snippet}
            date={msg.sentAt || msg.createdAt}
            isRead={msg.isRead}
            isStarred={msg.isStarred}
            hasAttachments={msg.hasAttachments}
            isSelected={selectedIds.has(msg.id)}
            isActive={msg.id === activeMessageId}
            onSelect={onSelectMessage}
            onClick={onClickMessage}
            onToggleStar={onToggleStar}
          />
        ))}
      </div>
    </div>
  );
}
